import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { ChevronRightIcon, CheckIcon } from '../../components/ui/Icons';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import type { User, UnlistedStock } from '../../types';

interface UnlistedStockDetailsProps {
    user?: User | null;
}

const UnlistedStockDetails: React.FC<UnlistedStockDetailsProps> = ({ user }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [stock, setStock] = useState<UnlistedStock | null>(null);
    const [loading, setLoading] = useState(true);
    const [interestForm, setInterestForm] = useState({
        quantity: 1,
        mobile: '',
        email: user?.email || '',
        message: '',
        submitted: false
    });
    const [errors, setErrors] = useState({
        quantity: '',
        mobile: '',
        email: ''
    });

    const validateField = (name: string, value: any) => {
        let error = '';
        if (name === 'quantity') {
            if (!value || value <= 0) {
                error = 'Quantity must be greater than 0';
            } else if (stock && (value * stock.price < 100000)) {
                error = `Min. investment is ₹1,00,000 (Current: ₹${(value * stock.price).toLocaleString()})`;
            }
        } else if (name === 'mobile') {
            const mobileRegex = /^[6-9]\d{9}$/;
            if (!value) {
                error = 'Mobile number is required';
            } else if (!mobileRegex.test(value)) {
                error = 'Enter a valid 10-digit number starting with 6-9';
            }
        } else if (name === 'email' && value && !user) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                error = 'Enter a valid email address';
            }
        }
        setErrors(prev => ({ ...prev, [name]: error }));
        return error;
    };

    // Update email if user logs in/out
    useEffect(() => {
        if (user?.email) {
            setInterestForm(prev => ({ ...prev, email: user.email }));
        }
    }, [user]);

    useEffect(() => {
        const fetchStock = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const headers: Record<string, string> = {};
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                const response = await fetch(`/api/unlisted-stocks/${id}`, {
                    headers: headers
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        setStock({
                            ...data.data,
                            price: Number(data.data.price),
                            min_investment: Number(data.data.min_investment)
                        });
                        // Auto-calculate initial quantity
                        if (data.data.price > 0 && data.data.min_investment > 0) {
                            const minQty = Math.ceil(data.data.min_investment / data.data.price);
                            setInterestForm(prev => ({ ...prev, quantity: minQty }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching stock:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchStock();
    }, [id]);

    // SEO Redirect: If accessing via UUID, redirect to slug URL
    useEffect(() => {
        if (stock && stock.slug && id && id !== stock.slug && id.length > 30) {
            navigate(`/grow/unlisted-stocks/${stock.slug}`, { replace: true });
        }
    }, [stock, id, navigate]);

    const handleInterestSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Final validation before submit
        const qErr = validateField('quantity', interestForm.quantity);
        const mErr = validateField('mobile', interestForm.mobile);
        const eErr = validateField('email', interestForm.email);

        if (qErr || mErr || eErr) {
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`/api/unlisted-stocks/${id}/interest`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    quantity: interestForm.quantity,
                    mobile: interestForm.mobile,
                    email: user?.email || interestForm.email,
                    message: interestForm.message
                })
            });

            if (response.ok) {
                setInterestForm({ ...interestForm, submitted: true });
            }
        } catch (error) {
            console.error('Error submitting interest:', error);
        }
    };

    if (loading) return <div className="p-20 flex justify-center"><LoadingSpinner /></div>;
    if (!stock) return <div className="p-20 text-center">Stock not found</div>;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
            <SEOHead
                title={stock.name}
                description={stock.description ? stock.description.replace(/<[^>]*>?/gm, '').substring(0, 160) : `Invest in ${stock.name} unlisted shares. Current Price: ₹${stock.price}. Sector: ${stock.sector}.`}
                canonicalUrl={`/grow/unlisted-stocks/${stock.slug}`}
                ogType="article"
                ogImage={stock.image_url ? (stock.image_url.startsWith('http') ? stock.image_url : `https://marketeye.in/api${stock.image_url}`) : undefined}
                structuredData={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "FinancialProduct",
                            "name": stock.name,
                            "sku": stock.symbol,
                            "description": stock.description ? stock.description.replace(/<[^>]*>?/gm, '') : undefined,
                            "brand": {
                                "@type": "Brand",
                                "name": stock.name
                            },
                            "offers": {
                                "@type": "Offer",
                                "price": stock.price,
                                "priceCurrency": "INR",
                                "availability": stock.status === 'ACTIVE' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                                "url": `https://marketeye.in/grow/unlisted-stocks/${stock.slug}`
                            }
                        },
                        {
                            "@type": "BreadcrumbList",
                            "itemListElement": [
                                { "@type": "ListItem", "position": 1, "name": "Grow", "item": "https://marketeye.in/grow" },
                                { "@type": "ListItem", "position": 2, "name": "Unlisted Stocks", "item": "https://marketeye.in/grow/unlisted-stocks" },
                                { "@type": "ListItem", "position": 3, "name": stock.name, "item": `https://marketeye.in/grow/unlisted-stocks/${stock.slug}` }
                            ]
                        }
                    ]
                }}
            />
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-6">
                <Link to="/grow" className="hover:text-trade-primary">Grow</Link>
                <ChevronRightIcon className="w-4 h-4" />
                <Link to="/grow/unlisted-stocks" className="hover:text-trade-primary">Unlisted Stocks</Link>
                <ChevronRightIcon className="w-4 h-4" />
                <span className="text-slate-900 font-medium">{stock.name}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Header Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 relative">
                        {(user?.isGrowthAdmin || user?.isUnlistedAdmin) && (
                            <Link
                                to={`/admin/unlisted-stocks/${stock.id}/edit`}
                                className="absolute top-4 right-4 text-xs font-medium text-slate-500 hover:text-trade-primary border border-slate-200 rounded px-2 py-1"
                            >
                                Edit Stock
                            </Link>
                        )}
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 mb-2">{stock.name}</h1>
                                <div className="flex items-center space-x-2 text-slate-500">
                                    <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                                        {stock.sector || 'Technology'}
                                    </span>
                                    <span>•</span>
                                    <span>{stock.symbol}</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-3xl font-bold text-slate-900">
                                    ₹{stock.price.toLocaleString()}
                                </p>
                                <p className="text-sm text-slate-500">per share</p>
                            </div>
                        </div>

                        {stock.image_url && (
                            <img
                                src={stock.image_url.startsWith('http') ? stock.image_url : `/api${stock.image_url}`}
                                alt={stock.name}
                                className="w-full h-64 object-cover rounded-lg mb-6 bg-slate-100"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stock.name)}&background=random&size=512`;
                                }}
                            />
                        )}

                        {/* Pricing Slabs */}
                        {stock.pricing_slabs && stock.pricing_slabs.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-slate-900 mb-3">Volume Pricing</h3>
                                <div className="overflow-hidden border border-slate-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-slate-200">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Minimum Quantity</th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Price per Share</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-slate-200">
                                            {stock.pricing_slabs.map((slab, idx) => (
                                                <tr key={idx}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{slab.min_qty.toLocaleString()}+</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">₹{slab.price.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="prose prose-slate max-w-none">
                            <h3 className="text-lg font-semibold text-slate-900 mb-3">About {stock.name}</h3>
                            <div dangerouslySetInnerHTML={{ __html: stock.description }} />
                        </div>
                    </div>

                    {/* Transaction Process */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8">
                        <h2 className="text-xl font-bold text-slate-900 mb-6">How This Works</h2>
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-900">Express Interest</h4>
                                    <p className="text-slate-600 text-sm mt-1">Submit your interest with the desired quantity. Our team will reach out to complete the documentation.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-900">Payment & Transfer</h4>
                                    <p className="text-slate-600 text-sm mt-1">Once the documentation is completed, you will receive payment details. Complete the payment and share the reference.</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-slate-900">Credit to Demat</h4>
                                    <p className="text-slate-600 text-sm mt-1">Shares will be credited to your Demat account within T+4 days of payment confirmation.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Interest Form */}
                    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 sticky top-24">
                        <h3 className="text-lg font-bold text-slate-900 mb-4">Interested in Investing?</h3>

                        {interestForm.submitted ? (
                            <div className="bg-green-50 text-green-700 p-4 rounded-lg flex items-start gap-3">
                                <CheckIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                <div>
                                    <p className="font-medium">Interest Recorded!</p>
                                    <p className="text-sm mt-1">Our team will reach out to you shortly with next steps.</p>
                                    <button
                                        onClick={() => setInterestForm({ ...interestForm, submitted: false })}
                                        className="text-sm font-medium text-green-800 mt-2 hover:underline"
                                    >
                                        Submit another request
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleInterestSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Quantity (Shares)
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={interestForm.quantity}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value) || 0;
                                            setInterestForm({ ...interestForm, quantity: val });
                                            validateField('quantity', val);
                                        }}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.quantity ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-blue-500'}`}
                                    />
                                    {errors.quantity ? (
                                        <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>
                                    ) : (
                                        <p className="text-xs text-slate-500 mt-1">
                                            Min. Investment: ₹1,00,000
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        required
                                        placeholder="Enter 10 digit number"
                                        value={interestForm.mobile}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setInterestForm({ ...interestForm, mobile: val });
                                            validateField('mobile', val);
                                        }}
                                        onBlur={(e) => validateField('mobile', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.mobile ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 focus:ring-blue-500'}`}
                                    />
                                    {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Email {user ? '' : '(Optional)'}
                                    </label>
                                    <input
                                        type="email"
                                        value={interestForm.email}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setInterestForm({ ...interestForm, email: val });
                                            if (!user) validateField('email', val);
                                        }}
                                        onBlur={(e) => {
                                            if (!user) validateField('email', e.target.value);
                                        }}
                                        placeholder="your@email.com"
                                        readOnly={!!user}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.email ? 'border-red-300 focus:ring-red-200' : (user ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'border-slate-200 focus:ring-blue-500')}`}
                                    />
                                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                                    {user && <p className="text-[10px] text-slate-400 mt-1 italic">Email linked to your account</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">
                                        Message (Optional)
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={interestForm.message}
                                        onChange={(e) => setInterestForm({ ...interestForm, message: e.target.value })}
                                        placeholder="Any specific questions?"
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full btn btn-primary py-2.5 bg-trade-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Express Interest
                                </button>
                                <p className="text-xs text-center text-slate-500">
                                    We'll reach out on your provided contact details with the next steps. No immediate payment required.
                                </p>
                            </form>
                        )}

                        <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500 space-y-3">
                            <p>
                                <span className="font-bold text-slate-700">Note:</span> This site only provides information on unlisted shares for reference purposes and does not function as a trading or broking platform. The information presented here is not a recommendation or investment advice, and users should conduct their own research before making any investments.
                            </p>
                            <p>
                                MarketEye holds shares in the companies listed here, and sells shares to interested buyers offline. SEBI rules do not permit online trading of unlisted shares.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnlistedStockDetails;
